import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Bus, CalendarClock } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { transportDashboardApi } from '@/services/api/endpoints/transport';
import { queryKeys } from '@/services/api/queryKeys';
import { ROUTES } from '@/app/router/routes';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { TransportAlert } from '@/types/transport';
import { formatDateTime } from '@/utils/format';
import { Badge, Stat, humanise } from './shared';

/**
 * Where transport stands today.
 *
 * <p>Every figure is one the database can state outright — a count, a status.
 * Nothing is projected. The alerts are the server's, rendered verbatim and in
 * the server's order (DANGER first): each names its subject and its clock, so
 * a person reads "Bus 7's insurance lapses on the 22nd" rather than a bare
 * count of troubles they then have to go and find.
 *
 * <p>The guardian-notice outbox appears only for holders of
 * TRANSPORT_ENROLLMENT_VIEW: every event names a student and a guardian's
 * phone number, and dashboard access alone must not surface that.
 */
export function TransportDashboardPage() {
  const { can } = useAuth();
  const seesEvents = can(PERMISSIONS.TRANSPORT_ENROLLMENT_VIEW);

  const dashboard = useQuery({
    queryKey: queryKeys.transport.dashboard,
    queryFn: () => transportDashboardApi.dashboard(),
  });

  const [eventsPage, setEventsPage] = useState(0);
  const [pendingOnly, setPendingOnly] = useState(false);
  const eventsParams = { page: eventsPage, size: 10, pending: pendingOnly };

  const events = useQuery({
    queryKey: queryKeys.transport.events(eventsParams),
    queryFn: () => transportDashboardApi.events(eventsParams),
    enabled: seesEvents,
  });

  if (dashboard.isError) {
    return <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />;
  }
  if (dashboard.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const data = dashboard.data;

  return (
    <div>
      <PageHeader
        title="Transport"
        description="The fleet, the network, today's trips and what needs attention."
        icon={<Bus className="h-5 w-5" aria-hidden="true" />}
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Fleet"
          value={data.totalVehicles}
          hint={`${data.activeVehicles} active · ${data.vehiclesUnderMaintenance} in the workshop`}
        />
        <Stat
          label="Routes"
          value={data.activeRoutes}
          hint={`of ${data.totalRoutes} defined`}
        />
        <Stat label="Drivers" value={data.activeDrivers} hint="Active driver profiles" />
        <Stat
          label="Riders"
          value={data.activeEnrollments}
          tone="success"
          hint={`${data.pendingEnrollments} pending · ${data.suspendedEnrollments} suspended`}
        />
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <Stat
          label="Trips today"
          value={data.tripsToday}
          hint={`${data.tripsCompletedToday} completed · ${data.tripsCancelledToday} cancelled`}
        />
        <Stat
          label="Unmarked attendance"
          value={data.tripsMissingAttendance}
          tone={data.tripsMissingAttendance > 0 ? 'danger' : 'neutral'}
          hint={
            data.tripsMissingAttendance > 0 ? (
              <Link
                to={ROUTES.transportOperations}
                className="text-accent underline-offset-2 hover:underline"
              >
                Trips with riders nobody marked
              </Link>
            ) : (
              'Every trip accounted for'
            )
          }
        />
        <Stat
          label="Overdue maintenance"
          value={data.overdueMaintenance}
          tone={data.overdueMaintenance > 0 ? 'warning' : 'neutral'}
          hint={
            data.overdueMaintenance > 0 ? (
              <Link
                to={ROUTES.transportMaintenance}
                className="text-accent underline-offset-2 hover:underline"
              >
                See what is due
              </Link>
            ) : (
              'Nothing past its interval'
            )
          }
        />
        <Stat
          label="Over capacity"
          value={data.overCapacityRoutes}
          tone={data.overCapacityRoutes > 0 ? 'danger' : 'neutral'}
          hint={
            data.overCapacityRoutes > 0 ? (
              <Link
                to={ROUTES.transportRoutes}
                className="text-accent underline-offset-2 hover:underline"
              >
                Routes holding more seats than they have
              </Link>
            ) : (
              'Every route within its seats'
            )
          }
        />
        <Stat
          label="Open incidents"
          value={data.openIncidents}
          tone={data.openIncidents > 0 ? 'danger' : 'neutral'}
          hint={
            data.openIncidents > 0 ? (
              <Link
                to={ROUTES.transportIncidents}
                className="text-accent underline-offset-2 hover:underline"
              >
                See the incidents
              </Link>
            ) : (
              'Nothing open'
            )
          }
        />
      </div>

      <div className={`mt-4 grid gap-4 ${seesEvents ? 'lg:grid-cols-2' : ''}`}>
        <Card className="p-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
            Needs attention
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            The server's words, in the server's order — danger first.
          </p>
          {data.alerts.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted">Nothing needs attention.</p>
          ) : (
            <ul className="mt-2 divide-y divide-line">
              {data.alerts.map((alert, index) => {
                const link = alertLink(alert);
                return (
                  <li key={`${alert.kind}-${alert.subjectId ?? index}-${index}`} className="py-2.5">
                    <div className="flex items-start gap-2">
                      <Badge tone={alert.level === 'DANGER' ? 'danger' : 'warning'}>
                        {humanise(alert.level)}
                      </Badge>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-ink">{alert.message}</p>
                        <p className="mt-0.5 text-[11px] uppercase tracking-[0.04em] text-subtle">
                          {humanise(alert.kind)}
                        </p>
                      </div>
                      {link !== undefined && (
                        <Link
                          to={link}
                          className="shrink-0 text-xs font-semibold text-accent underline-offset-2 hover:underline"
                        >
                          Open
                        </Link>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {seesEvents && (
          <Card className="p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                Guardian notices
              </h2>
              <label className="flex items-center gap-1.5 text-xs text-muted">
                <input
                  type="checkbox"
                  checked={pendingOnly}
                  onChange={(event) => {
                    setPendingOnly(event.target.checked);
                    setEventsPage(0);
                  }}
                  className="h-3.5 w-3.5 border-line"
                />
                Undelivered only
              </label>
            </div>
            <p className="mt-0.5 text-xs text-muted">
              The transport outbox: what a guardian was — or would have been — told, inspectable
              rather than merely stored.
            </p>

            {events.isError ? (
              <ErrorState error={events.error} onRetry={() => void events.refetch()} />
            ) : events.isPending ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : events.data.data.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted">
                {pendingOnly ? 'Nothing is waiting to be delivered.' : 'No notices recorded yet.'}
              </p>
            ) : (
              <>
                <ul className="mt-2 divide-y divide-line">
                  {events.data.data.map((event) => (
                    <li key={event.id} className="py-2.5">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-ink">
                            {humanise(event.eventType)}
                          </span>
                          <span className="block truncate text-xs text-muted">
                            {event.recipientName ?? 'Unnamed recipient'}
                            {event.recipientPhone !== undefined && ` · ${event.recipientPhone}`}
                          </span>
                        </span>
                        <span className="shrink-0 text-right">
                          <span className="block text-xs tabular-nums text-muted">
                            {formatDateTime(event.occurredAt)}
                          </span>
                          <span className="mt-0.5 block">
                            {event.dispatchedAt === undefined ? (
                              <Badge tone="warning">not delivered</Badge>
                            ) : (
                              <Badge tone="success">delivered</Badge>
                            )}
                          </span>
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <span className="text-xs text-muted">
                    {events.data.pagination.totalElements} notice
                    {events.data.pagination.totalElements === 1 ? '' : 's'}
                  </span>
                  <span className="flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={events.data.pagination.first}
                      onClick={() => setEventsPage((current) => Math.max(0, current - 1))}
                    >
                      Newer
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={events.data.pagination.last}
                      onClick={() => setEventsPage((current) => current + 1)}
                    >
                      Older
                    </Button>
                  </span>
                </div>
              </>
            )}
          </Card>
        )}
      </div>

      {can(PERMISSIONS.TRANSPORT_TRIP_VIEW) && (
        <div className="mt-4">
          <Link
            to={ROUTES.transportOperations}
            className="inline-flex items-center gap-2 border border-line bg-bg px-3 py-2 text-sm font-semibold text-ink hover:border-accent"
          >
            <CalendarClock className="h-4 w-4" aria-hidden="true" />
            Open the day board
          </Link>
        </div>
      )}
    </div>
  );
}

/**
 * Where an alert's subject lives. The kind decides the screen; an alert
 * without a subject (or about a screen that has no detail page) simply does
 * not link. The link is a courtesy — the message already says everything.
 */
function alertLink(alert: TransportAlert): string | undefined {
  if (alert.subjectId === undefined) {
    return undefined;
  }
  switch (alert.kind) {
    case 'VEHICLE_DOCUMENT':
      return ROUTES.vehicleDetail(alert.subjectId);
    case 'DRIVER_DOCUMENT':
    case 'DRIVER_LICENSE':
      return ROUTES.transportDriverDetail(alert.subjectId);
    case 'MAINTENANCE':
      return ROUTES.transportMaintenance;
    case 'CAPACITY':
      return ROUTES.transportRouteDetail(alert.subjectId);
    case 'ATTENDANCE':
      return ROUTES.tripDetail(alert.subjectId);
  }
}
